// #3392 : UPS 레이블 + 홀박스 목록·레이블을 미리 렌더하는 Stimulsoft 어댑터.
// 렌더가 끝났는지(완결성)는 여기서 판정하지 않는다. snapshotRenderedReport 로 옮긴 순수 객체를 renderCompleteness.js 가 본다.
// window.Stimulsoft 는 호출 시점에 참조한다(번들이 index.html 에서 전역으로 로드된다).

import { WHOLE_BOX_TICKET_REQUIRED_NAMES } from '@/utils/renderCompleteness';

const DEFAULT_TIMERS = {
	setTimeout: (fn, ms) => setTimeout(fn, ms),
	clearTimeout: id => clearTimeout(id),
};

/**
 * #3392 : 데이터를 등록하고 renderAsync 를 한 번만 settle 하는 Promise 로 감싼다.
 * - 콜백 오류, 동기 예외, 감시 시간 초과는 reject 다. 콜백 null 은 resolve 지만 완료를 뜻하지 않는다.
 * - 콜백 안에서는 settle 만 한다. 콜백이 던지면 renderAsync 가 콜백을 한 번 더 부른다.
 * - 렌더하는 동안 StiError.errorMessageForm 을 비워 Stimulsoft 오류창이 toast 와 함께 뜨지 않게 한다.
 *   전역 공유 속성이므로 복원은 한 번만, 그리고 값이 아직 우리가 넣은 null 일 때만 한다(그 사이 뷰어가 새 폼을 등록했으면 두지 않는다).
 * 반환 { promise, restore }: restore 는 settle 전에 실행을 버릴 때(abandon) 호출한다.
 */
export function renderWithData(report, data, watchdog_ms, timers = DEFAULT_TIMERS) {
	const Stimulsoft = window.Stimulsoft;
	const sti_error = Stimulsoft.System.StiError;
	let saved_form = null;
	let form_cleared = false;
	let restored = false;
	let settled = false;
	let timer = null;
	let resolve_render;
	let reject_render;
	const promise = new Promise((resolve, reject) => {
		resolve_render = resolve;
		reject_render = reject;
	});

	const restore = () => {
		if (restored) return;
		restored = true;
		if (form_cleared && sti_error.errorMessageForm === null) sti_error.errorMessageForm = saved_form;
	};

	const settle = error => {
		if (settled) return;
		settled = true;
		if (timer !== null) timers.clearTimeout(timer);
		timer = null;
		restore();
		if (error) reject_render(error);
		else resolve_render(report);
	};

	try {
		saved_form = sti_error.errorMessageForm;
		sti_error.errorMessageForm = null;
		form_cleared = true;

		// 엔진은 이 값을 지우지 않으므로 렌더 전에 비운다.
		report.reportRenderingMessages = null;
		const data_set = new Stimulsoft.System.Data.DataSet();
		data_set.readJson(data);
		report.regData('', '', data_set);

		timer = timers.setTimeout(() => settle(new Error('Render watchdog timeout (' + watchdog_ms + 'ms)')), watchdog_ms);
		report.renderAsync(error => {
			try {
				settle(error == null ? null : error instanceof Error ? error : new Error(String(error)));
			} catch (e) {
				// 콜백에서 던지지 않는다.
			}
		});
	} catch (e) {
		settle(e instanceof Error ? e : new Error(String(e)));
	}

	return { promise, restore };
}

function findByName(list, name) {
	return list.filter(c => c && c.name === name);
}

/**
 * #3392 : WholeBoxTicket 템플릿(Millimeters)을 렌더 전에 HundredthsOfInch 로 바꾼다. UPS 보고서에 쪽을 붙여도 4x6 이 되게 하려는 것이다.
 * 사전 검사: 단위 Millimeters, 페이지 1개 Page3, root_wholeBoxPrintList 데이터소스, DataBand3 아래 leaf 이름 = 고정 15개.
 * 반환 { ok, reason }. 예외는 그대로 던진다.
 */
export function convertToHundredthsOfInch(report) {
	const Stimulsoft = window.Stimulsoft;
	const unit_type = Stimulsoft.Report.StiReportUnitType;
	const StiContainer = Stimulsoft.Report.Components.StiContainer;

	if (report.reportUnit !== unit_type.Millimeters) return { ok: false, reason: 'template unit is not Millimeters' };
	if (!report.pages || report.pages.count !== 1) return { ok: false, reason: 'template page count is not 1' };
	const page = report.pages.getByIndex(0);
	if (!page || page.name !== 'Page3') return { ok: false, reason: 'template page is not Page3' };
	if (!report.dictionary.dataSources.getByName('root_wholeBoxPrintList')) return { ok: false, reason: 'no root_wholeBoxPrintList data source' };
	const bands = findByName(page.getComponents().list, 'DataBand3');
	if (bands.length !== 1) return { ok: false, reason: 'no DataBand3' };
	const leaf_names = bands[0]
		.getComponents()
		.list.filter(c => !(c instanceof StiContainer))
		.map(c => c.name);
	const required = new Set(WHOLE_BOX_TICKET_REQUIRED_NAMES);
	if (leaf_names.length !== required.size || new Set(leaf_names).size !== leaf_names.length || !leaf_names.every(name => required.has(name))) {
		return { ok: false, reason: 'DataBand3 leaves differ: ' + leaf_names.join(',') };
	}

	// 이 setter 가 템플릿의 쪽·컴포넌트 값을 실제로 변환한다.
	report.reportUnit = unit_type.HundredthsOfInch;

	const converted = report.pages.getByIndex(0);
	if (!(Math.abs(converted.pageWidth - 400) <= 1) || !(Math.abs(converted.pageHeight - 600) <= 1)) {
		return { ok: false, reason: 'converted page size is ' + converted.pageWidth + 'x' + converted.pageHeight };
	}
	return { ok: true, reason: '' };
}

/**
 * #3392 : 렌더된 보고서를 순수 객체로 옮긴다. 값은 그대로 둔다(컴포넌트 누락·null·undefined·빈 문자열을 구별한다).
 * 컴포넌트는 page.getComponents().list 순서다.
 */
export function snapshotRenderedReport(report) {
	const Stimulsoft = window.Stimulsoft;
	const StiText = Stimulsoft.Report.Components.StiText;
	const StiImage = Stimulsoft.Report.Components.StiImage;
	const StiBarCode = Stimulsoft.Report.BarCodes ? Stimulsoft.Report.BarCodes.StiBarCode : null;
	const unit_type = Stimulsoft.Report.StiReportUnitType;
	const report_unit = report.reportUnit;

	const pages = [];
	const rendered = report.renderedPages;
	for (let index = 0; index < rendered.count; ++index) {
		const page = rendered.getByIndex(index);
		const components = page.getComponents().list.map(c => {
			const item = { name: c.name, type: 'other', enabled: c.enabled !== false };
			if (StiText && c instanceof StiText) {
				item.type = 'text';
				item.text = c.text;
			} else if (StiBarCode && c instanceof StiBarCode) {
				item.type = 'barcode';
				item.code_value = c.codeValue;
			} else if (StiImage && c instanceof StiImage) {
				item.type = 'image';
				item.has_image = c.imageToDraw != null;
			}
			return item;
		});
		pages.push({
			name: page.name,
			page_width: page.pageWidth,
			page_height: page.pageHeight,
			segment_per_width: page.segmentPerWidth,
			segment_per_height: page.segmentPerHeight,
			components,
		});
	}

	return {
		report_unit,
		report_unit_name: typeof report_unit === 'number' && unit_type ? unit_type[report_unit] : String(report_unit),
		rendering_message_count: (report.reportRenderingMessages || []).length,
		pages,
	};
}

// #3392 : 렌더된 쪽을 다른 보고서 뒤에 붙인다(ReportViewer.reportListMerge 와 같은 방식).
export function appendRenderedPages(target, source) {
	for (let index = 0; index < source.renderedPages.count; ++index) {
		target.renderedPages.add(source.renderedPages.getByIndex(index));
	}
}

// #3392 : 렌더된 쪽 객체를 순서대로 배열로 옮긴다(재배치 전 원래 객체를 고정하고 재배치 뒤 확인한다).
export function renderedPageList(report) {
	const pages = [];
	for (let index = 0; index < report.renderedPages.count; ++index) {
		pages.push(report.renderedPages.getByIndex(index));
	}
	return pages;
}

// #3392 : 렌더된 쪽을 주어진 순서로 제자리에서 바꾼다. setByIndex 만 쓴다(add 는 쪽 좌표를 다시 변환하므로 렌더된 쪽을 다시 붙이지 않는다).
export function reorderRenderedPages(report, pages) {
	const rendered = report.renderedPages;
	if (!Array.isArray(pages) || pages.length !== rendered.count) throw new Error('Reorder page count ' + (Array.isArray(pages) ? pages.length : 'none') + ' != ' + rendered.count);
	for (let index = 0; index < pages.length; ++index) {
		rendered.setByIndex(index, pages[index]);
	}
}
